import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles"
import PhoneInput, { getCountryCallingCode } from "react-phone-number-input"
import "react-phone-number-input/style.css"
import nlPhoneLabels from "react-phone-number-input/locale/nl.json"
import { injectIntl, Link } from "gatsby-plugin-intl"
import { CTA_BUTTON_PRIMARY } from "../styles/cta"
import { ATTRIBUTION_FIELDS, loadAttribution } from "./formAttribution"

// A Netlify form of its own, so it cannot affect the contact or quote forms.
const FORM_NAME = "follow-up"

// Room the hero keeps under its headline for the bar on desktop (see
// heroContent in index.js).
export const FOLLOW_UP_ROOM = 84

const FIELD_HEIGHT = 46
const ROW_GAP = 10

// On desktop the bar rests with its bottom edge `hang` below the hero. It is
// placed by its top edge, so a longer message grows it downward, not upward.
const ONE_ROW = { rows: 1, padTop: 16, padBottom: 22, hang: 46 }
const TWO_ROWS = { rows: 2, padTop: 14, padBottom: 14, hang: 62 }
const restingHeight = ({ rows, padTop, padBottom }) =>
  padTop + rows * FIELD_HEIGHT + (rows - 1) * ROW_GAP + padBottom + 2
const barTop = (layout) =>
  `calc(100% + ${layout.hang - restingHeight(layout)}px)`

const COLORS = {
  panel: "#E7E3E1",
  panelLine: "rgba(42,42,42,0.06)",
  shadow:
    "0 -14px 40px -24px rgba(0,0,0,0.35), 0 28px 50px -26px rgba(0,0,0,0.6)",
  title: "#2A2A2A",
  accent: "#919B91",
  text: "#2A2A2A",
  textSoft: "#444444",
  field: "#F3EFEE",
  fieldFocus: "#F8F5F4",
  line: "#C9C1BB",
  placeholder: "#666666",
  focus: "#B8923F",
  focusRing: "rgba(201,162,75,0.22)",
  privacy: "#686360",
  error: "#A8423A",
}

const themeVars = (theme) =>
  Object.fromEntries(
    Object.entries(theme).map(([k, v]) => [
      `--fu-${k.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase())}`,
      v,
    ])
  )

const useStyles = makeStyles((theme) => {
  const stacked = theme.breakpoints.down("sm")
  const twoRows = "@media (min-width:960px) and (max-width:1359px)"
  const oneRow = "@media (min-width:1360px)"
  const narrow = "@media (max-width:359px)"
  const fieldBox = {
    boxSizing: "border-box",
    background: "var(--fu-field)",
    border: "1px solid var(--fu-line)",
    borderRadius: 6,
    transition:
      "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
  }
  const fieldFocus = {
    borderColor: "var(--fu-focus)",
    boxShadow: "0 0 0 3px var(--fu-focus-ring)",
    background: "var(--fu-field-focus)",
  }
  return {
    wrap: {
      zIndex: 5,
      [theme.breakpoints.up("md")]: {
        position: "absolute",
        left: 32,
        right: 32,
        maxWidth: 1320,
        margin: "0 auto",
      },
      [twoRows]: { top: barTop(TWO_ROWS) },
      [oneRow]: { top: barTop(ONE_ROW) },
      [stacked]: { position: "relative", marginTop: theme.spacing(3.5) },
    },
    form: {
      position: "relative",
      maxWidth: 1320,
      margin: "0 auto",
      boxSizing: "border-box",
      color: "var(--fu-text)",
      background: "var(--fu-panel)",
      border: "1px solid var(--fu-panel-line)",
      borderRadius: 10,
      boxShadow: "var(--fu-shadow)",
      display: "grid",
      gap: 12,
      [oneRow]: {
        gridTemplateColumns:
          "120px minmax(0,1fr) minmax(0,1fr) minmax(0,1.6fr) minmax(0,1.3fr) minmax(0,1.8fr) 150px",
        gridTemplateAreas: '"label first last phone email message submit"',
        alignItems: "start",
        columnGap: 14,
        padding: `${ONE_ROW.padTop}px 24px ${ONE_ROW.padBottom}px`,
      },
      [twoRows]: {
        gridTemplateColumns:
          "120px minmax(0,1fr) minmax(0,1fr) minmax(0,1.3fr) 150px",
        gridTemplateAreas:
          '"label first last phone submit" "label email message message submit"',
        alignItems: "start",
        columnGap: 14,
        rowGap: ROW_GAP,
        padding: `${TWO_ROWS.padTop}px 22px ${TWO_ROWS.padBottom}px`,
      },
      [stacked]: {
        gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
        gridTemplateAreas:
          '"label label" "first last" "phone phone" "email email" "message message" "submit submit"',
        padding: "22px 18px 18px",
        rowGap: 12,
        columnGap: 10,
      },
      [narrow]: {
        gridTemplateColumns: "minmax(0,1fr)",
        gridTemplateAreas:
          '"label" "first" "last" "phone" "email" "message" "submit"',
      },
    },
    label: {
      gridArea: "label",
      margin: 0,
      color: "var(--fu-title)",
      textTransform: "uppercase",
      letterSpacing: "0.2em",
      fontSize: "0.72rem",
      fontWeight: 600,
      lineHeight: 1.55,
      "&::after": {
        content: '""',
        display: "block",
        flexShrink: 0,
        width: 22,
        height: 1,
        marginTop: 9,
        background: "var(--fu-accent)",
      },
      // A fixed height, so a title that wraps to three lines (Dutch) cannot
      // make the bar taller than its fields.
      [oneRow]: {
        height: FIELD_HEIGHT,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        lineHeight: 1.4,
      },
      [twoRows]: { marginTop: 4 },
      [stacked]: { marginBottom: 4 },
    },
    visuallyHidden: {
      position: "absolute",
      width: 1,
      height: 1,
      overflow: "hidden",
      clip: "rect(0 0 0 0)",
      whiteSpace: "nowrap",
    },
    field: { position: "relative", minWidth: 0 },
    input: {
      ...fieldBox,
      display: "block",
      width: "100%",
      height: FIELD_HEIGHT,
      padding: "0 14px",
      color: "var(--fu-text)",
      fontFamily: "inherit",
      fontSize: 16,
      outline: "none",
      "&::placeholder": { color: "var(--fu-placeholder)", opacity: 1 },
      "&:focus": fieldFocus,
      "&:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0 1000px var(--fu-field) inset",
        WebkitTextFillColor: "var(--fu-text)",
        caretColor: "var(--fu-text)",
      },
      [stacked]: { height: 50 },
    },
    invalid: { borderColor: "var(--fu-error)" },
    error: {
      display: "block",
      marginTop: 4,
      color: "var(--fu-error)",
      fontSize: "0.7rem",
      lineHeight: 1.3,
    },
    message: {
      height: "auto",
      padding: "11px 14px",
      lineHeight: "22px",
      resize: "none",
      overflow: "hidden",
      transition:
        "height 220ms cubic-bezier(0.2, 0.7, 0.2, 1), border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
      [stacked]: { padding: "13px 14px" },
    },
    // Invisible copy of the message, measured so the textarea can animate to
    // the height its text needs instead of jumping.
    sizer: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      visibility: "hidden",
      pointerEvents: "none",
      boxSizing: "border-box",
      border: "1px solid transparent",
      padding: "11px 14px",
      fontSize: 16,
      lineHeight: "22px",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      [stacked]: { padding: "13px 14px" },
    },
    phoneBox: {
      ...fieldBox,
      display: "flex",
      alignItems: "stretch",
      height: FIELD_HEIGHT,
      "&:focus-within": fieldFocus,
      "& .PhoneInput": { display: "flex", flex: 1, minWidth: 0 },
      "& input": {
        flex: 1,
        minWidth: 0,
        background: "transparent",
        border: 0,
        outline: "none",
        color: "var(--fu-text)",
        fontFamily: "inherit",
        fontSize: 16,
        padding: "0 12px 0 10px",
        "&::placeholder": { color: "var(--fu-placeholder)", opacity: 1 },
      },
      [stacked]: { height: 50 },
    },
    country: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 5,
      padding: "0 8px 0 11px",
      borderRight: "1px solid var(--fu-line)",
      color: "var(--fu-text-soft)",
      fontSize: 14,
      whiteSpace: "nowrap",
      "& select": {
        position: "absolute",
        inset: 0,
        width: "100%",
        opacity: 0,
        cursor: "pointer",
        fontSize: 16,
      },
      "& .PhoneInputCountryIcon": {
        width: 20,
        height: 14,
        boxShadow: "none",
        background: "none",
      },
    },
    submitCell: {
      gridArea: "submit",
      [twoRows]: { alignSelf: "center" },
    },
    submit: {
      ...CTA_BUTTON_PRIMARY,
      width: "100%",
      minHeight: FIELD_HEIGHT,
      height: FIELD_HEIGHT,
      padding: "0 24px",
      fontSize: 15,
      whiteSpace: "nowrap",
      [stacked]: { height: 52, minHeight: 52, marginTop: 4 },
    },
    privacy: {
      margin: "8px 0 0",
      color: "var(--fu-privacy)",
      fontSize: "0.68rem",
      lineHeight: 1.4,
      textAlign: "center",
      "& a": { color: "inherit", textDecoration: "underline" },
      // One line in the gap under the fields, pinned to the bar's bottom so a
      // longer message pushes it down instead of running into it.
      [oneRow]: {
        position: "absolute",
        right: 24,
        bottom: 5,
        margin: 0,
        fontSize: "0.64rem",
        lineHeight: "12px",
        whiteSpace: "nowrap",
      },
      [twoRows]: { margin: "6px 0 0", fontSize: "0.64rem" },
      [stacked]: { fontSize: "0.72rem" },
    },
    honeypot: {
      display: "none",
      visibility: "hidden",
      height: 0,
      overflow: "hidden",
      position: "absolute",
    },
  }
})

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Flag, dialling code and a chevron over an invisible native select, so the
// country list stays the phone's own picker and the number field can show
// its placeholder.
const CountrySelect = ({
  value,
  onChange,
  options,
  iconComponent: Icon,
  disabled,
  readOnly,
  className,
  onFocus,
  onBlur,
  "aria-label": ariaLabel,
}) => (
  <div className={className}>
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value || undefined)}
      disabled={disabled || readOnly}
      aria-label={ariaLabel}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {options.map(({ value: v, label, divider }) => (
        <option
          key={divider ? "divider" : v || "international"}
          value={divider ? "|" : v || ""}
          disabled={divider}
        >
          {label}
        </option>
      ))}
    </select>
    <Icon country={value} label="" aria-hidden="true" />
    <span aria-hidden="true">
      {value ? `+${getCountryCallingCode(value)}` : "+"}
    </span>
    <svg viewBox="0 0 10 10" aria-hidden="true" width="9" height="9">
      <path
        d="M1 3l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  </div>
)
CountrySelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
  iconComponent: PropTypes.elementType,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  "aria-label": PropTypes.string,
}

const FollowUpBar = ({ intl }) => {
  const c = useStyles()
  const t = (id) => intl.formatMessage({ id })
  const [state, setState] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    message: "",
  })
  const [touched, setTouched] = useState({})
  const [attribution, setAttribution] = useState({})
  const [messageHeight, setMessageHeight] = useState(null)
  const [messageScrolls, setMessageScrolls] = useState(false)
  const sizerRef = useRef(null)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setAttribution(loadAttribution())
    setHydrated(true)
  }, [])

  // On desktop the bar hangs over the next section, so the message grows to
  // four lines there and scrolls after that; on phones it grows freely.
  useLayoutEffect(() => {
    if (!sizerRef.current) return
    const needed = sizerRef.current.offsetHeight
    const cap = window.matchMedia("(min-width:960px)").matches ? 112 : Infinity
    setMessageHeight(Math.min(needed, cap))
    setMessageScrolls(needed > cap)
  }, [state.message])

  const error = (id) => {
    if (!touched[id]) return ""
    const v = state[id]
    if (!v || !v.trim()) return t("home.followUp.required")
    if (id === "email" && !emailRegex.test(v))
      return t("home.followUp.invalidEmail")
    return ""
  }

  const set = (id) => (e) =>
    setState((prev) => ({ ...prev, [id]: e.target.value }))
  const blur = (id) => () => setTouched((prev) => ({ ...prev, [id]: true }))

  const handleSubmit = (e) => {
    e.preventDefault()
    setTouched({ firstName: true, lastName: true, phone: true, email: true })
    const ok =
      ["firstName", "lastName", "phone"].every((id) => state[id].trim()) &&
      emailRegex.test(state.email)
    if (!ok) return
    try {
      const userData = { email: state.email, phone: state.phone }
      const name = `${state.firstName} ${state.lastName}`.trim()
      if (name) userData.fullName = name
      sessionStorage.setItem("formUserData", JSON.stringify(userData))
    } catch (err) {
      /* storage unavailable: the conversion still fires on /thankyou */
    }
    e.target.submit()
  }

  const textField = (id, area, type, autoComplete, required = true) => (
    <div className={c.field} style={{ gridArea: area }}>
      <label className={c.visuallyHidden} htmlFor={`fu-${id}`}>
        {t(`home.followUp.${id}`)}
      </label>
      <input
        id={`fu-${id}`}
        name={id}
        type={type}
        autoComplete={autoComplete}
        placeholder={t(`home.followUp.${id}`)}
        value={state[id]}
        onChange={set(id)}
        onBlur={blur(id)}
        required={required}
        aria-invalid={Boolean(error(id))}
        className={`${c.input} ${error(id) ? c.invalid : ""}`}
      />
      {error(id) && <span className={c.error}>{error(id)}</span>}
    </div>
  )

  return (
    <div id="follow-up" className={c.wrap}>
      <form
        name={FORM_NAME}
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        action="/thankyou"
        onSubmit={handleSubmit}
        // As on the contact form: native `required` blocks empty submits
        // until React has mounted, then the React validation takes over.
        noValidate={hydrated}
        className={c.form}
        style={themeVars(COLORS)}
      >
        <input type="hidden" name="form-name" value={FORM_NAME} />
        <input
          type="hidden"
          name="subject"
          value={`Website: Get in touch (home page, ${intl.locale.toUpperCase()})`}
        />
        <p className={c.honeypot}>
          <label>
            Leave this field empty: <input name="bot-field" />
          </label>
        </p>
        {ATTRIBUTION_FIELDS.map((f) => (
          <input key={f} type="hidden" name={f} value={attribution[f] || ""} />
        ))}

        <h2 className={c.label}>{t("home.followUp.title")}</h2>

        {textField("firstName", "first", "text", "given-name")}
        {textField("lastName", "last", "text", "family-name")}

        <div className={c.field} style={{ gridArea: "phone" }}>
          <label className={c.visuallyHidden} htmlFor="fu-phone">
            {t("home.followUp.phone")}
          </label>
          <div className={`${c.phoneBox} ${error("phone") ? c.invalid : ""}`}>
            <PhoneInput
              id="fu-phone"
              defaultCountry="NL"
              addInternationalOption={false}
              labels={intl.locale === "nl" ? nlPhoneLabels : undefined}
              placeholder={t("home.followUp.phone")}
              value={state.phone}
              onChange={(v) =>
                setState((prev) => ({ ...prev, phone: v || "" }))
              }
              onBlur={blur("phone")}
              countrySelectComponent={CountrySelect}
              countrySelectProps={{ className: c.country }}
              autoComplete="tel"
              required
            />
          </div>
          <input type="hidden" name="phone" value={state.phone} />
          {error("phone") && <span className={c.error}>{error("phone")}</span>}
        </div>

        {textField("email", "email", "email", "email")}

        <div className={c.field} style={{ gridArea: "message" }}>
          <label className={c.visuallyHidden} htmlFor="fu-message">
            {t("home.followUp.message")}
          </label>
          <textarea
            id="fu-message"
            name="message"
            rows={1}
            placeholder={t("home.followUp.message")}
            value={state.message}
            onChange={set("message")}
            className={`${c.input} ${c.message}`}
            style={
              messageHeight
                ? {
                    height: messageHeight,
                    overflowY: messageScrolls ? "auto" : "hidden",
                  }
                : undefined
            }
          />
          <div ref={sizerRef} className={c.sizer} aria-hidden="true">
            {state.message + "​"}
          </div>
        </div>

        <div className={c.submitCell}>
          <button type="submit" className={c.submit}>
            {t("home.followUp.submit")} →
          </button>
          {/* Stamped "yes" on mount: empty means the page's JS never ran (bot). */}
          <input
            type="hidden"
            name="sent-from-browser"
            value={hydrated ? "yes" : ""}
          />
          <p className={c.privacy}>
            {t("home.followUp.privacy")}
            <Link to="/privacy-policy/">{t("home.followUp.privacyLink")}</Link>.
          </p>
        </div>
      </form>
    </div>
  )
}

export default injectIntl(FollowUpBar)
